import * as Sentry from '@sentry/browser';
import { all, call, put, select } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';
import { generateTakeLatestSaga } from 'utils/sagas';
import menus, { collections as staticCollections, findByName as staticFindByName, typeCapsules as staticTypeCapsules } from 'content/menus';

// Lodash
import {
	each,
	filter,
	find,
	get,
	includes,
	isArray,
	isEmpty,
	map
} from 'lodash';

import { dataLayer } from 'utils/analytics';
import {
	getCart as uGetCart,
	extractCartData as uExtractCartData,
	addToCart as uAddToCart,
	removeFromCart as uRemoveFromCart,
	updateCartItems as uUpdateCartItems
} from 'utils/cart';
import { isAuthenticated as authenticated } from 'utils/authentication';
import { errorCodes, licenseTypes } from 'config';
import request from 'utils/request';
import { removeFavorite, addFavorite } from 'utils/favorites';

import { mailingListSubscribeUnsubscribe as mailingListSubscribeUnsubscribeLanguage } from 'language';

import { clearUser, getUser } from 'store/account/actions';

import {
	ADD_TO_CART,
	CLEAR_MENU,
	CLOSE_AUTHENTICATION_MODAL,
	DELETE_PROMO_CODE,
	GET_CART,
	GET_PRICES,
	LOAD_ADDITIONAL_MENU_ITEMS,
	LOAD_COLLECTION,
	LOAD_DEFAULT_SUB_MENU,
	LOAD_FAVORITES,
	LOAD_SEARCH,
	PRELOAD_SUBMENUS,
	REMOVE_FROM_CART,
	SET_AUTH_STATE,
	SET_MENU,
	SET_PROMO_CODE,
	SET_SUB_MENU,
	SUBSCRIBE_NEWSLETTER,
	TOGGLE_FAVORITE,
	UPDATE_CART_ITEMS,
	UNSUBSCRIBE_NEWSLETTER,
	GET_POLICY_TEXT,
	LOAD_CONTACT_TEXT_DISPLAY
} from './constants';

import {
	cartItemsUpdateError,
	cartItemsUpdateSuccess,
	cartUpdateError,
	cartUpdateSuccess,
	clear,
	clearFavorites,
	clearMenu,
	clearMenuContents,
	clearPromoCode,
	clearSubMenu,
	clearSubMenuContents,
	collectionLoaded,
	collectionLoadingError,
	collectionsLoaded,
	collectionsLoadingError,
	favoritesLoaded,
	favoritesLoadingError,
	getCart,
	getCartError,
	getCartSuccess,
	getPricesError,
	getPricesLoaded,
	isMenuOpen,
	loadAdditionalMenuItems,
	loadAdditionalMenuItemsClear,
	loadAdditionalMenuItemsError,
	loadAdditionalMenuItemsLoaded,
	loadCollections,
	loadDefaultSubMenu,
	loadFavorites,
	loadFindByName,
	loadFindByNameLoaded,
	loadFindByNameLoadingError,
	loadTypeCapsules,
	loadTypeCapsulesLoaded,
	loadTypeCapsulesLoadingError,
	loadMenuError,
	loadMenuSuccess,
	loadSubMenuError,
	loadSubMenuSuccess,
	openAuthenticationModal,
	preloadSubMenusError,
	preloadSubMenusSucess,
	searchLoaded,
	searchLoadingError,
	setError,
	setErrorMessage,
	setErrorModal,
	setLoading,
	setPromoCodeError,
	setSubMenu,
	setSubMenuSnapshot,
	toggleFavoriteError,
	toggleFavoriteHolding,
	toggleFavoriteSuccess,
	setPolicyTextSucess,
	setPolicyTextError,
	setPolicyTextLoading,
	contactTextLoaded,
	contactTextLoadingError
} from './actions';

import { updateLicense } from 'store/licenses/actions';
import { selectLicenseState } from 'store/licenses/selectors';
import {
	selectCart,
	selectCollection,
	selectFavoritedPackageIDs,
	selectFavoriteHolding,
	selectFavorites,
	selectLocationState,
	selectPreloadedSubMenus,
	selectPreloadedSubMenusLoaded,
	selectSubMenuSnapshot
} from './selectors';

// Load cart
export function* handleGetCart(action) {
	const type = 'handleGetCart';

	try {
		// Send request for cart items
		const cart = yield call(uGetCart, action.data);

		// Get any licenses present in the cart
		const licenses = {
			app: { inCart: get(cart, 'data.has_app', false) },
			cloud: { inCart: get(cart, 'data.has_cloud', false) }
		};


		yield all([
			put(updateLicense(licenses)),
			put(getCartSuccess(cart.data))
		]);
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		// Ensure cart loading-related errors only appear on the cart page
		// TODO: Cover route changes
		const location = yield select(selectLocationState());
		const route = get(location, 'locationBeforeTransitions.pathname', false);
		if (route.includes('/cart')) {
			yield all([
				put(setError({ type, error: message })),
				put(setErrorModal())
			]);
		}
		yield put(getCartError(err));
	}
}

/**
 * Add one or more items to the cart
 * @method  addToCart
 * @param   {Object}  action  Data sent over by the action
 * @return  {Generator}
 *
 * Cart items can come in a variety of formats. All are run through a validator before being
 * sent to the API. Here are a few examples of cart item formats:
 *
 * 		SINGLE ITEM
 * 		computer license:
 *
 * 			{
 * 				item_id: 200132,
 * 				quantity: 1
 * 			}
 *
 * 		cloud license:
 *
 * 			{
 * 				item_id: "200170",
 * 				item_type: "cloud",
 * 				value: "1",
 * 				quantity: 1
 * 			}
 *
 * 		app license:
 *
 * 			{
 * 				item_id: "200219",
 * 				item_type: "app",
 * 				quantity: 1
 * 			}
 *
 * 		MULTIPLE ITEMS
 * 		computer license + self-hosted license:
 *
 * 			[
 * 				{
 * 					item_id: 200176,
 * 					quantity: 10
 * 				}, {
 * 					item_id: 200176,
 * 					item_type: "selfHosted",
 * 					self_hosted_pageview_id: 11
 * 				}
 * 			]
 *
 * 		computer and self-hosted licenses, custom package for both:
 *
 * 			[
 * 				{
 * 					item_id: [200132, 200102],
 * 					quantity: 1
 * 				}, {
 * 					item_id: [200132, 200102],
 * 					item_type: "selfHosted",
 * 					self_hosted_pageview_id: 1
 * 				}
 * 			]
 */
export function* addToCart(action) {
	try {
		const licenses = yield select(selectLicenseState());
		const cartItems = action.data;

		// Validate and clean the data passed-in
		let cleanData;

		// Multiple items are passed in.
		if (isArray(cartItems)) {
			const items = [];
			// Cycle through each item and clean the data
			each(cartItems, (cartItem) => {
				const prepared = uExtractCartData({ licenses, ...cartItem });
				items.push(prepared);
				return prepared;
			});

			// Some return data will be an Array more than one level deep (for instance, when adding
			// a custom package with self-hosting selected). Here we pull the nested arrays up to
			// the top for later stringifying.
			cleanData = [].concat(...items);
		} else {
			cleanData = yield call(uExtractCartData, { licenses, ...cartItems });
		}

		// If `cleanData` comes back as an array, stringify
		if (isArray(cleanData)) {
			cleanData = { items: JSON.stringify(cleanData) };
		}

		// Set computer licenses to “1” if the user adds an app license to the cart and doesn’t
		// have any computers selected
		if (
			get(cleanData, 'type') === 'app'
			&& !get(licenses, 'computer.value')
		) {
			yield put(updateLicense({ computer: { value: 1 } }));
		}

		// Main call to add item to cart
		const response = yield call(uAddToCart, cleanData);

		yield all([
			put(cartUpdateSuccess()),
			put(getCart())
		]);

		let newCartItems = [];
		if (isArray(response.data.data)) {
			newCartItems = response.data.data.map((item) => item.attributes);
		} else {
			newCartItems = [response.data.data.attributes];
		}

		dataLayer({ ecommerce: null });
		dataLayer({
			event: 'eec_add',
			item_name: newCartItems[0].item_name,
			ecommerce: {
				items: newCartItems.map((item) => {
					const {
						item_id: id,
						license_type: licenseType,
						item_name: name,
						price,
						quantity: itemQuantity,
						self_hosted_pageview_id: selfHostedID
					} = item;

					let category = 'fonts';
					if (licenseType === 'cloud_license' || licenseType === 'app_license') {
						category = 'subscriptions';
					}
					let variant = `${itemQuantity} ${itemQuantity === 1? 'computer' : 'computers'}`;
					if (licenseType === 'self_hosted_license') {
						variant = selfHostedID === 1 ? '250,000 pv/m' : '1,000,000 pv/mo'; 
					}
					return {
						item_name: name,
						item_id: id,
						item_brand: 'Hoefler&Co',
						item_category: category,
						item_variant: `${licenseType} - ${variant}`,
						price,
						quantity: 1,
						currency: 'USD',
					};
				})
			}
		});

		if (action.callback) {
			yield call(action.callback);
		}
	} catch (err) {
		// What type of error checking do we really need here?
		const message = get(err, 'response.data.error.message', err.message);
		const code = get(err, 'response.data.error.code');

		yield all([
			put(setErrorModal(message)),
			put(cartUpdateError(err))
		]);

		if (code === errorCodes.INVALID_ARGUMENT) {
			yield put(getCart());
		}
	}
}

// Remove from cart handler
export function* removeFromCart(action) {
	// Get the source data from the cart
	const cartItemID = get(action, 'data.ids');

	// Match the cart item against a licenseType
	const type = get(action, 'data.type');
	// Ensure the type passed in is a valid license type with an `inCart` attribute
	const typeIsValid = !!find(licenseTypes, item => item.shortname === type);
	const cart = yield select(selectCart());
	const cartItem = find(cart.items, { id: cartItemID });

	try {
		// If a sourceLicense was found, get the shortname and use it to update licenses
		if (type && typeIsValid) {
			const updatedLicense = {
				[type]: {
					focused: false,
					inCart: false
				}
			};
			// Remove item from licenses if necessary
			yield put(updateLicense(updatedLicense));
		}

		// Main call to remove item from cart
		yield call(uRemoveFromCart, { id: cartItemID });
		yield all([
			put(cartUpdateSuccess()),
			put(getCart())
		]);
		if (cartItem) {
			const {
				itemID: id,
				name,
				licenseType,
				price,
				quantity: itemQuantity,
				selfHostedID
			} = cartItem;

			let category = 'fonts';
			if (licenseType === 'cloud_license' || licenseType === 'app_license') {
				category = 'subscriptions';
			}
			let variant = `${itemQuantity} ${itemQuantity === 1? 'computer' : 'computers'}`;
			if (licenseType === 'self_hosted_license') {
				variant = selfHostedID === 1 ? '250,000 pv/m' : '1,000,000 pv/mo'; 
			}

			dataLayer({ ecommerce: null });
			dataLayer({
				event: 'eec_remove',
				item_name: name,
				ecommerce: {
					items: [
						{
							item_name: name,
							item_id: id,
							item_brand: 'Hoefler&Co',
							item_category: category,
							item_variant: `${licenseType} - ${variant}`,
							price,
							quantity: 1,
							currency: 'USD'
						}
					]
				}
			});
		}
	} catch (err) {
		// What type of error checking do we really need here?
		yield put(cartUpdateError(err));
	}
}

// Prices request/response handler
export function* getPrices(action) {
	// Select packages from store
	const licenses = yield select(selectLicenseState());

	// Stop the getPrices request if no licenses appear to be in the cart or subscribed
	const activeLicenses = filter(licenses, item => item.inCart || item.subscribed || item.value);
	if (!activeLicenses.length) { return; }

	try {
		const response = yield call(request, '/api/v1/items', {
			params: {
				quantity: licenses.computer.value === 0 ? '' : licenses.computer.value,
				ids: action.packageIDs,
				fields: 'price_difference',
				self_hosted_pageview_id: licenses.selfHosted.value
			}
		});

		// Filter response to only include items that match the IDs in `action.packageIDs`
		const filtered = filter(response.data.data, item => includes(action.packageIDs, item.id));

		const prices = map(filtered, item => ({
			extend_price: item.attributes.extend_price,
			cloud_only_price: item.attributes.cloud_only_price,
			id: item.id,
			old_price: item.attributes.old_price,
			price_difference: item.attributes.price_difference,
			price: item.attributes.price,
			unit_price: item.attributes.unit_price
		}));

		yield put(getPricesLoaded(prices));
	} catch (err) {
		yield put(getPricesError(err.message));
	}
}

export function* getFavorites(action) {
	try {
		const { options: { include } } = action;
		const response = yield call(request, '/api/v1/favorites', {
			params: {
				fields: 'item_ids',
				include,
				serializer: 'array'
			}
		});
		yield put(favoritesLoaded(response.data));
	} catch (err) {
		yield put(favoritesLoadingError(err));
	}
}

export function* closeAuthModal() {
	try {
		if (authenticated()) {
			const favoriteHolding = yield select(selectFavoriteHolding());
			if (favoriteHolding) { yield* toggleFavorite({ data: { itemID: favoriteHolding } }); }
		}
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield all([
			put(setErrorModal(message)),
			put(cartUpdateError(err))
		]);
	}
}

export function* toggleFavorite(action) {
	let response;


	// Acceptable “methods” to use.
	const methods = ['add', 'remove', 'toggle'];


	// Check to see which method is passed in. If no method is passed, default
	// to “toggle”.
	let method = get(action, 'data.action', 'toggle');


	// Package ID that was passed in.
	const id = Number(action.data.itemID);


	// Should we update favorites with the latest data from the API? All
	// “toggle” type methods can update, but modifications to favorites on the
	// “Favorites” page should not refresh. We have items on this page that
	// require being in a different state locally since you can remove favorites
	// without them actually disappearing from view. This setting is local by
	// default but could be exposed as an action parameter if more granular
	// control is needed.
	const refreshAfterResponse = method === 'toggle';


	// If no method was passed in, error out
	if (methods.indexOf(method) < 0) { throw Error('In order remove or add a favorite you must set the `action` property.'); }


	// If the method is `toggle` we’ll need to check the store and see if the
	// visitor already has this package favorited. If so, delete the favorite.
	// If not, add it.
	if (method === 'toggle') {
		let isFavorited = false;

		// Get an array of the user’s current favorites
		const favoritedPackageIDs = yield select(selectFavoritedPackageIDs());

		// Determine whether or not the favorite passed in is already in that array.
		if (favoritedPackageIDs) {
			isFavorited = favoritedPackageIDs.indexOf(id) > -1;
		}

		method = isFavorited ? 'remove' : 'add';
	}


	try {
		if (method === 'remove') {
			const favorites = yield select(selectFavorites());
			const existingFavorite = find(favorites, (item) => item.itemId === id);
			response = yield call(removeFavorite, { itemID: existingFavorite.id });
		} else if (method === 'add') {
			response = yield call(addFavorite, { itemID: id });
		}

		yield put(toggleFavoriteSuccess());
		yield put(toggleFavoriteHolding(null));

		if (refreshAfterResponse) {
			yield put(loadFavorites());
		}

		yield put(getUser());

		if (action.callback) {
			yield call(action.callback, response);
		}
	} catch (err) {
		if (!authenticated()) {
			yield put(toggleFavoriteHolding(id));
			yield put(openAuthenticationModal({ isAddingFavorite: true }));
		} else {
			yield put(toggleFavoriteError(err));
		}
	}
}

export function* updateCartItems(action) {
	console.log(action.data);
	try {
		// Main call to remove item from cart
		yield call(uUpdateCartItems, action.data);
		yield all([
			put(cartItemsUpdateSuccess()),
			put(getCart({ showReconciliation: true }))
		]);
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield all([
			put(setErrorModal(message)),
			put(cartItemsUpdateError(err))
		]);
	}
}

export function* setIsAuthenticated({ isAuthenticated }) {
	if (isAuthenticated) {
		try {
			yield all([
				put(loadFavorites()),
				put(getUser())
			]);
		} catch (error) {
			if (process.env.NODE_ENV === 'development' && process.env.LOG_ERRORS) {
				throw error;
			} else {
				Sentry.withScope(scope => {
					scope.setExtra('debug', false);
					Sentry.captureException(error);
				});
			}
		}
	} else if (!isAuthenticated) {
		try {
			yield all([
				put(clearUser()),
				put(clearFavorites())
			]);
		} catch (err) {
			yield put(setErrorMessage());
		}
	}
}

// Collections
export function* getCollections() {
	const url = '/api/v1/collections?serializer=array';

	try {
		yield put(loadCollections());
		const response = yield call(request, url);
		const updatedCollections = {
			...staticCollections,
			preloaded: true,
			sections: response.data.collections
		};
		yield put(collectionsLoaded(updatedCollections));
	} catch (err) {
		yield put(collectionsLoadingError(err.message));
	}
}

// Additional Collections
export function* getAdditionalMenuItems(data) {
	if (data.section === undefined) { return; }

	const preloadedSubMenus = yield select(selectPreloadedSubMenus());
	const preloadedSubMenusLoaded = yield select(selectPreloadedSubMenusLoaded());

	try {
		// Check if this particular section of additional menu items are already
		// in redux store. If yes, load that instead of initialize an API call.
		if (preloadedSubMenusLoaded && !isEmpty(preloadedSubMenus.additional[data.section])) {
			yield put(loadAdditionalMenuItemsLoaded({ section: data.section, data: [...preloadedSubMenus.additional[data.section]] }));
		} else {
			const url = `/api/v1/${data.section}?show_additional=true`;
			const response = yield call(request, url);
			yield put(loadAdditionalMenuItemsLoaded({ section: data.section, data: [...response.data.data] }));
		}
	} catch (err) {
		yield put(loadAdditionalMenuItemsError(err.message));
	}
}

export function* getCollection() {
	const currentCollection = yield select(selectCollection());
	if (currentCollection) {
		const url = `/api/v1/collections/${currentCollection.id}?include=styles,related_collections,entry&serializer=array`;
		try {
			const response = yield call(request, url);
			yield put(collectionLoaded(response.data));
		} catch (err) {
			yield put(collectionLoadingError(err.message));
		}
	}
}

// Menus
export function* loadMenu(action) {
	try {
		const requestedMenu = action.data;
		const subMenuSnapshot = yield select(selectSubMenuSnapshot());

		// If an empty string is passed in, clear the menu contents
		if (isEmpty(requestedMenu)) {
			yield all([
				put(clearMenuContents()),
				put(isMenuOpen(false))
			]);
		} else {
			const menu = menus[requestedMenu];
			yield put(isMenuOpen(true));

			const defaultSection = get(menu, 'defaultSection');
			if (defaultSection) {
				// targeting separate menu in the menu config file
				yield put(loadDefaultSubMenu({ menu: subMenuSnapshot || defaultSection, counted: true }));
			} else {
				// targeting same menu that was requested (which means it has a “content” section included)
				yield put(loadDefaultSubMenu({ menu: requestedMenu, counted: true }));
			}

			// If the menu source should come from the API…
			if (menu && menu.source === 'api') {
				const url = `/api/v1/${requestedMenu}?serializer=array`;
				const response = yield call(request, url);
				menu.sections = get(response, `data.${requestedMenu}`);
				yield put(loadMenuSuccess(menu));
			} else {
				// The menu contents should come from a local source
				yield put(loadMenuSuccess(menu));
			}
		}
	} catch (err) {
		yield put(loadMenuError(err.message));
	}
}

export function* loadSubMenu(action) {
	// const counted = yield select(selectCounted());
	const preloadedSubMenus = yield select(selectPreloadedSubMenus());
	const preloadedSubMenusLoaded = yield select(selectPreloadedSubMenusLoaded());

	try {
		if (!preloadedSubMenusLoaded) { return; }
		const requestedMenu = action.data.menu;

		// By default, used the static menus that's imported from a file at the top of the screen.
		let menu = menus[requestedMenu];

		// If an API preloaded font submenu is available for this specific menu request,
		// then use it instead. Right now, only collections and findByName are preloaded.
		if (preloadedSubMenusLoaded && !isEmpty(preloadedSubMenus[requestedMenu])) {
			menu = preloadedSubMenus[requestedMenu];
		}

		// NOTE: Right now this is calling the API every time 'Browse by Style'
		// is loaded. Ideally this should be cached into the
		// preloadedSubMenu.collections but it would require more modifications
		// of other components. A separate ticket perhaps?
		//
		// Check if additional is present and if this was already fetched previously,
		if (!isEmpty(menu.additional)) {
			yield put(loadAdditionalMenuItems(requestedMenu));
		} else {
			yield put(loadAdditionalMenuItemsClear());
		}

		if (menu === undefined) { throw new Error('No menu attached.'); }

		// Save snapshot of subMenu if requested menu is at level 2
		// Example: If the user closes the 'font' primary nav item with 'findByName'
		// being the last submenu shown, the next time 'font' nav item is clicked,
		// the submenu will load 'findByName' content durectly via this snapshot
		if (menu.menuLevel === 2) {
			yield put(setSubMenuSnapshot(requestedMenu));
		}

		yield put(clearSubMenuContents());
		yield put(loadSubMenuSuccess(menu));
	} catch (err) {
		yield put(loadSubMenuError(err.message));
	}
}

export function* loadDefSubMenu(action) {
	const subMenu = action.data.menu;

	try {
		yield put(setSubMenu({ menu: subMenu, counted: true }));
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield all([
			put(setErrorModal(message)),
			put(cartItemsUpdateError(err))
		]);
	}
}

export function* clearMenus() {
	try {
		yield all([
			put(clearMenuContents()),
			put(clearSubMenu()),
			put(clearSubMenuContents())
		]);
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield all([
			put(setErrorModal(message)),
			put(cartItemsUpdateError(err))
		]);
	}
}

export function* locationChange() {
	try {
		yield put(clearMenu());
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		yield all([
			put(setErrorModal(message)),
			put(cartItemsUpdateError(err))
		]);
	}
}

export function* getSearch(action) {
	const { term } = action.data;
	const url = `/api/v1/search/query/${term}`;
	try {
		let data = null;
		if (term && term !== '') {
			const response = yield call(request, url);
			data = response.data.body;
		}
		yield put(searchLoaded(data, action.layout));
		if (action.callback) {
			yield call(action.callback);
		}
	} catch (err) {
		yield put(searchLoadingError(err.message, action.layout));
	}
}

function* subscribeUnsubscribeNewsletter(action) {
	const type = action.type === SUBSCRIBE_NEWSLETTER ? 'subscribe' : 'unsubscribe';
	try {
		const url = `/api/v1/newsletter/${type}`;
		yield call(request, url, {
			method: 'POST',
			body: action.data
		});
		if (action.callback) {
			yield call(action.callback);
		}
	} catch (err) {
		if (action.type === 'subscribe') {
			yield put(setErrorModal(
				mailingListSubscribeUnsubscribeLanguage[type].error.message,
				mailingListSubscribeUnsubscribeLanguage[type].error.title
			));
		}
	}
}

export function* getFindByName() {
	const url = '/api/v1/findByName?serializer=array';

	try {
		yield put(loadFindByName());
		const response = yield call(request, url);
		// each font object needs to be reshaped or supplemented
		// with additional properties to match the static version
		// of findByName object found in 'menus' object.
		const reshapedSection = response.data.find_by_name.map((font) => {
			const updatedFont = {
				...font,
				section: null,
				icon: null,
				link: `/fonts/${font.slug}`,
				secondaryMenu: null,
				text: font.name
			};

			return updatedFont;
		});

		const updatedFindByName = {
			...staticFindByName,
			preloaded: true,
			sections: reshapedSection
		};
		yield put(loadFindByNameLoaded(updatedFindByName));
	} catch (err) {
		yield put(loadFindByNameLoadingError(err.message));
	}
}

export function* getTypeCapsules() {
	const url = '/api/v1/typeCapsules?serializer=array';

	try {
		yield put(loadTypeCapsules());
		const response = yield call(request, url);
		// each font object needs to be reshaped or supplemented
		// with additional properties to match the static version
		// of findByName object found in 'menus' object.

		const reshapedSection = response.data.type_capsules.map((capsule) => {
			const updatedCapsule = {
				...capsule,
				section: null,
				icon: null,
				svg: `https://d31td5fkd89rr1.cloudfront.net${capsule.svg}`,
				link: `/fonts/${capsule.slug}`,
				secondaryMenu: null,
				text: capsule.name,
				new: capsule.new === 1 ? 'new' : null,
				price: capsule.price
			};

			return updatedCapsule;
		});
		const updatedTypeCapsules = {
			...staticTypeCapsules,
			preloaded: true,
			sections: reshapedSection
		};

		yield put(loadTypeCapsulesLoaded(updatedTypeCapsules));
	} catch (err) {
		yield put(loadTypeCapsulesLoadingError(err.message));
	}
}

export function* getSubMenus() {
	try {
		// NOTE: Additional calls can be made in "all" to other sagas to preload
		// other subMenus (see either one of the sages above for example).
		yield all([
			getCollections(),
			getFindByName(),
			getTypeCapsules(),
			getAdditionalMenuItems({ section: 'collections' })
		]);
		yield put(preloadSubMenusSucess());
	} catch (err) {
		yield put(preloadSubMenusError(err.message));
	}
}

export function* setPromoCode({ data, options }) {
	const type = 'setPromoCode';
	const cart = yield select(selectCart());

	try {
		// We send an action that tells Redux we're sending a request
		yield all([
			put(clear({ type })),
			put(clearPromoCode()),
			put(setLoading({ type, loading: true }))
		]);

		yield call(request, `/api/v1/cart/${cart.id}/promo_code`, {
			method: 'POST',
			body: {
				promo_code: data.code
			}
		});

		yield put(getCart());

		if (options && options.success) {
			yield call(options.success);
		}
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);
		const status = get(err, 'response.data.error.code');

		yield all([
			put(setPromoCodeError({ status, message })),
			put(setError({ type, error: message }))
		]);
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}

export function* deletePromoCode({ data, options }) {
	const type = 'deletePromoCode';
	const cart = yield select(selectCart());

	try {
		yield call(request, `/api/v1/cart/${cart.id}/promo_code/${data.code}`, {
			method: 'DELETE'
		});

		yield put(getCart());

		if (options && options.success) {
			yield call(options.success);
		}
	} catch (err) {
		const message = get(err, 'response.data.error.message', err.message);

		yield put(setError({ type, error: message }));
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put(setLoading({ type, loading: false }));
	}
}


export function* getPolicyText(action) {
	let channel = '';
	if (action.typeName === 'terms') {
		channel = 'site_terms';
	} else if (action.typeName === 'eula') {
		channel = 'eula_terms';
	} else if (action.typeName === 'web-fonts-terms') {
		channel = 'web_fonts_terms';
	} else if (action.typeName === 'cloud-terms') {
		channel = 'cloud_terms';
	} else if (action.typeName === 'app-terms') {
		channel = 'app_terms';
	} else if (action.typeName === 'privacy') {
		channel = 'privacy_terms';
	}

	const url = `/api/v1/cms/entries?channel=${channel}&sort=-updated_date&limit=1`;
	try {
		yield put(setPolicyTextLoading());
		const response = yield call(request, url);
		const data = response.data.data[0].attributes.content;
		yield put(setPolicyTextSucess({
			eyebrow: data[`${channel}_eyebrow`],
			content: data[`${channel}_content`],
			page_title: data[`${channel}_page_title`]
		}));
	} catch (err) {
		yield put(setPolicyTextError(err));
	}
}

export function* getContactDisplayText() {
	const url = '/api/v1/cms/entries?channel=contact_us_text&sort=-created_date&status=open&limit=10';
	try {
		const response = yield call(request, url);
		yield put(
			contactTextLoaded(
				response.data.data[0].attributes.content.text_to_display
			)
		);
	} catch (err) {
		yield put(contactTextLoadingError(err.message));
	}
}

/**
 * Root saga watchers
 *
 * Since we're code-splitting on routes and load the saga with each route
 * change, we need to ensure that the sagas aren't run more than once.
 * ___________________________________________________________________________
 */

export const addToCartSaga = generateTakeLatestSaga(ADD_TO_CART, addToCart);
export const clearMenuSaga = generateTakeLatestSaga(CLEAR_MENU, clearMenus);
export const closeAuthModalSaga = generateTakeLatestSaga(CLOSE_AUTHENTICATION_MODAL, closeAuthModal);
export const deletePromoCodeSaga = generateTakeLatestSaga(DELETE_PROMO_CODE, deletePromoCode);
export const getAdditionalMenuItemsSaga = generateTakeLatestSaga(LOAD_ADDITIONAL_MENU_ITEMS, getAdditionalMenuItems);
export const getCartSaga = generateTakeLatestSaga(GET_CART, handleGetCart);
export const getCollectionSaga = generateTakeLatestSaga(LOAD_COLLECTION, getCollection);
export const getFavoritesSaga = generateTakeLatestSaga(LOAD_FAVORITES, getFavorites);
export const getPricesSaga = generateTakeLatestSaga(GET_PRICES, getPrices);
export const getSearchSaga = generateTakeLatestSaga(LOAD_SEARCH, getSearch);
export const loadDefSubMenuSaga = generateTakeLatestSaga(LOAD_DEFAULT_SUB_MENU, loadDefSubMenu);
export const loadMenuSaga = generateTakeLatestSaga(SET_MENU, loadMenu);
export const loadSubMenuSaga = generateTakeLatestSaga(SET_SUB_MENU, loadSubMenu);
export const locationChangeSaga = generateTakeLatestSaga(LOCATION_CHANGE, locationChange);
export const preloadSubMenuSaga = generateTakeLatestSaga(PRELOAD_SUBMENUS, getSubMenus);
export const removeFromCartSaga = generateTakeLatestSaga(REMOVE_FROM_CART, removeFromCart);
export const setIsAuthenticatedSaga = generateTakeLatestSaga(SET_AUTH_STATE, setIsAuthenticated);
export const setPromoCodeSaga = generateTakeLatestSaga(SET_PROMO_CODE, setPromoCode);
export const subscribeNewsletterSaga = generateTakeLatestSaga(SUBSCRIBE_NEWSLETTER, subscribeUnsubscribeNewsletter);
export const unsubscribeNewsletterSaga = generateTakeLatestSaga(UNSUBSCRIBE_NEWSLETTER, subscribeUnsubscribeNewsletter);
export const toggleFavoritesSaga = generateTakeLatestSaga(TOGGLE_FAVORITE, toggleFavorite);
export const updateCartItemsSaga = generateTakeLatestSaga(UPDATE_CART_ITEMS, updateCartItems);
export const getPolicyTextSaga = generateTakeLatestSaga(GET_POLICY_TEXT, getPolicyText);

export const getContactDisplayTextSaga = generateTakeLatestSaga(
	LOAD_CONTACT_TEXT_DISPLAY,
	getContactDisplayText
);

// Bootstrap sagas
export default [
	addToCartSaga,
	closeAuthModalSaga,
	deletePromoCodeSaga,
	getAdditionalMenuItemsSaga,
	getCartSaga,
	getCollectionSaga,
	getFavoritesSaga,
	getPricesSaga,
	getSearchSaga,
	loadDefSubMenuSaga,
	loadMenuSaga,
	loadSubMenuSaga,
	locationChangeSaga,
	preloadSubMenuSaga,
	removeFromCartSaga,
	setIsAuthenticatedSaga,
	setPromoCodeSaga,
	subscribeNewsletterSaga,
	toggleFavoritesSaga,
	updateCartItemsSaga,
	unsubscribeNewsletterSaga,
	getPolicyTextSaga,
	getContactDisplayTextSaga
];
